import React, { useEffect, useRef, useState } from 'react';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { api, API, getToken } from '@/lib/api';
import { TID } from '@/constants/testIds';
import { Upload, Save, ShieldCheck, User, DollarSign, Store, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import BadgeCabinet from '@/components/BadgeCabinet';
import PublicProfileEditor from '@/components/PublicProfileEditor';
import { useLang } from '@/context/LangContext';
import { useAuth } from '@/context/AuthContext';

/**
 * Player profile view — backed by the same roster row the coach sees, so
 * any edit here propagates to /roster instantly. Player can change name,
 * jersey number, position, height/weight, dominant hand, strengths and
 * photo. They cannot move themselves between teams.
 */
function PlayerProfile() {
  const { t } = useLang();
  const [row, setRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get('/roster/me/self');
      setRow(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('error_generic'));
    }
  };
  useEffect(() => { load(); }, []);

  const upd = (k, v) => setRow(p => ({ ...p, [k]: v }));

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fileId = r.data.id;
      const token = getToken();
      upd('photo_url', `${API}/files/serve/${fileId}${token ? `?auth=${token}` : ''}`);
      toast.success(t('photo_uploaded'));
    } catch { toast.error(t('upload_error')); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/roster/me/self', {
        name: row.name,
        number: Number(row.number) || 0,
        position: row.position || '',
        height_cm: Number(row.height_cm) || 0,
        weight_kg: Number(row.weight_kg) || 0,
        dominant_hand: row.dominant_hand || 'right',
        strength: row.strength || '',
        weakness: row.weakness || '',
        ideal_role: row.ideal_role || '',
        photo_url: row.photo_url || null,
      });
      toast.success(t('profile_saved'));
    } catch { toast.error(t('error_generic')); }
    finally { setSaving(false); }
  };

  if (!row) return <Layout><div className="text-gray-400">{t('loading')}…</div></Layout>;

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('profile_eyebrow')}</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl flex items-center gap-3">
          <User className="w-9 h-9 text-gold" /> {t('profile_player_h1')}
        </h1>
        <p className="text-xs text-gray-400 mt-1">{t('profile_player_subtitle')}</p>
      </div>

      <div className="bg-card-steel border border-gold/30 rounded-lg p-6 space-y-3 max-w-2xl" data-testid="player-profile">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-32 h-32 rounded-lg border-2 border-gold bg-navy-deep overflow-hidden flex items-center justify-center">
            {row.photo_url ? (
              <img src={row.photo_url} alt="profile" className="w-full h-full object-cover" data-testid="player-profile-photo" />
            ) : (
              <span className="text-gold/40 text-xs">{t('no_photo')}</span>
            )}
          </div>
          <div className="flex-1">
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={e => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
            <button disabled={uploading} onClick={() => fileRef.current?.click()}
              data-testid="player-profile-upload-photo"
              className="text-xs px-3 py-2 rounded border border-gold/40 text-gold hover:bg-gold/10 inline-flex items-center gap-1">
              <Upload className="w-3 h-3" /> {uploading ? '...' : t('upload_photo_btn')}
            </button>
            <p className="text-xs text-gray-400 mt-2">{t('profile_player_photo_help')}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('roster_name') || 'Nombre'}</label>
            <input data-testid="player-profile-name" value={row.name || ''} onChange={e => upd('name', e.target.value)}
              className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2 text-white outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('roster_number') || 'Dorsal'}</label>
            <input type="number" data-testid="player-profile-number" value={row.number ?? ''} onChange={e => upd('number', e.target.value)}
              className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2 text-white outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('roster_position') || 'Posición'}</label>
            <input data-testid="player-profile-position" value={row.position || ''} onChange={e => upd('position', e.target.value)}
              className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2 text-white outline-none" placeholder="PG / SG / SF / PF / C" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('roster_height') || 'Altura (cm)'}</label>
            <input type="number" data-testid="player-profile-height" value={row.height_cm ?? ''} onChange={e => upd('height_cm', e.target.value)}
              className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2 text-white outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('roster_weight') || 'Peso (kg)'}</label>
            <input type="number" data-testid="player-profile-weight" value={row.weight_kg ?? ''} onChange={e => upd('weight_kg', e.target.value)}
              className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2 text-white outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('roster_dominant_hand') || 'Mano dominante'}</label>
            <select data-testid="player-profile-hand" value={row.dominant_hand || 'right'} onChange={e => upd('dominant_hand', e.target.value)}
              className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2 text-white outline-none">
              <option value="right">{t('roster_hand_right') || 'Derecha'}</option>
              <option value="left">{t('roster_hand_left') || 'Izquierda'}</option>
              <option value="ambi">{t('roster_hand_ambi') || 'Ambidiestro'}</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('roster_strength') || 'Fortaleza'}</label>
            <input data-testid="player-profile-strength" value={row.strength || ''} onChange={e => upd('strength', e.target.value)}
              className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2 text-white outline-none" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('roster_weakness') || 'A mejorar'}</label>
            <input data-testid="player-profile-weakness" value={row.weakness || ''} onChange={e => upd('weakness', e.target.value)}
              className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2 text-white outline-none" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('roster_ideal_role') || 'Rol ideal'}</label>
            <input data-testid="player-profile-ideal-role" value={row.ideal_role || ''} onChange={e => upd('ideal_role', e.target.value)}
              className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2 text-white outline-none" />
          </div>
        </div>

        <button data-testid="player-profile-save" onClick={save} disabled={saving}
          className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light">
          <Save className="w-4 h-4" /> {saving ? '...' : t('save_profile_btn')}
        </button>
      </div>
    </Layout>
  );
}

export default function Profile() {
  const { user } = useAuth();
  if (user?.role === 'player') return <PlayerProfile />;
  return <CoachProfile />;
}

function CoachProfile() {
  const { t } = useLang();
  const [profile, setProfile] = useState(null);
  const [myPlays, setMyPlays] = useState([]);
  const [achievements, setAchievements] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [me, plays, ach] = await Promise.all([
      api.get('/profile/me'),
      api.get('/plays?scope=mine'),
      api.get('/achievements/me').catch(() => ({ data: null })),
    ]);
    setProfile(me.data);
    setMyPlays(plays.data.plays || []);
    setAchievements(ach.data);
  };
  useEffect(() => { load(); }, []);

  const upd = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fileId = r.data.id;
      const token = getToken();
      upd('photo_url', `${API}/files/serve/${fileId}${token ? `?auth=${token}` : ''}`);
      toast.success(t('photo_uploaded'));
    } catch { toast.error(t('upload_error')); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/profile/me', profile);
      toast.success(t('profile_saved'));
    } catch { toast.error(t('error_generic')); }
    finally { setSaving(false); }
  };

  const publish = async (play) => {
    const price = prompt(`${t('price_prompt')} — "${play.name}":`, '4.99');
    if (!price) return;
    const blurb = prompt(t('sale_blurb_prompt'), play.description?.slice(0, 80) || '');
    try {
      await api.post(`/marketplace/publish/${play.id}`, { price: parseFloat(price), listing_blurb: blurb || '', listing_title: play.name });
      toast.success(t('play_published'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('error_generic'));
    }
  };

  const unpublish = async (play) => {
    await api.post(`/marketplace/unpublish/${play.id}`);
    toast.success(t('play_unpublished'));
    load();
  };

  if (!profile) return <Layout><div className="text-gray-400">{t('loading')}</div></Layout>;

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('profile_eyebrow')}</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl flex items-center gap-3">
          <User className="w-9 h-9 text-gold" /> {t('profile_h1')}
          {profile.is_elite && <ShieldCheck className="w-7 h-7 text-gold" title={t('elite_verified_badge')} />}
        </h1>
        {profile.is_elite && (
          <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gold/15 border border-gold text-gold text-xs uppercase tracking-widest font-h">
            <ShieldCheck className="w-3 h-3" /> {t('elite_verified_badge')}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-card-steel border border-gold/30 rounded-lg p-6 space-y-3">
          <h2 className="font-h uppercase text-xl text-gold mb-2">{t('identity_section')}</h2>

          <div className="flex items-start gap-4 mb-4">
            <div className="w-32 h-32 rounded-lg border-2 border-gold bg-navy-deep overflow-hidden flex items-center justify-center">
              {profile.photo_url ? (
                <img src={profile.photo_url} alt="profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-gold/40 text-xs">{t('no_photo')}</span>
              )}
            </div>
            <div className="flex-1">
              <input ref={fileRef} type="file" accept="image/*" className="hidden"
                onChange={e => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
              <button disabled={uploading} onClick={() => fileRef.current?.click()}
                data-testid="profile-upload-photo"
                className="text-xs px-3 py-2 rounded border border-gold/40 text-gold hover:bg-gold/10 inline-flex items-center gap-1">
                <Upload className="w-3 h-3" /> {uploading ? '...' : t('upload_photo_btn')}
              </button>
              <p className="text-xs text-gray-400 mt-2">{t('photo_help')}</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { k: 'display_name', tk: 'field_display_name' },
              { k: 'team', tk: 'field_team' },
              { k: 'country', tk: 'field_country' },
              { k: 'credentials', tk: 'field_credentials' },
              { k: 'social_x', tk: 'field_twitter' },
              { k: 'social_instagram', tk: 'field_instagram' },
              { k: 'social_website', tk: 'field_website' },
            ].map(f => (
              <div key={f.k}>
                <label className="text-xs uppercase tracking-widest text-gold font-mono">{t(f.tk)}</label>
                <input data-testid={`profile-${f.k}`} value={profile[f.k] || ''} onChange={e => upd(f.k, e.target.value)}
                  className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2 text-white outline-none" />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('field_bio')}</label>
              <textarea data-testid="profile-bio" value={profile.bio || ''} onChange={e => upd('bio', e.target.value)} rows={4}
                className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2 text-white outline-none resize-none" />
            </div>
          </div>

          <button data-testid="profile-save" onClick={save} disabled={saving}
            className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light">
            <Save className="w-4 h-4" /> {saving ? '...' : t('save_profile_btn')}
          </button>
        </div>

        <div className="bg-card-steel border-2 border-gold rounded-lg p-5">
          <h3 className="font-h uppercase text-lg text-gold mb-2 flex items-center gap-1">
            <ShieldCheck className="w-4 h-4" /> {t('elite_status')}
          </h3>
          {profile.is_elite ? (
            <div>
              <div className="text-2xl font-h text-gold mb-1">{t('verified_state')}</div>
              <p className="text-sm text-gray-300 mb-3">{t('elite_active_text')}</p>
              <div className="text-xs text-gray-400">{t('elite_since')} {profile.elite_since?.slice(0, 10)}</div>
              <Link to="/marketplace" className="mt-4 block px-4 py-2 rounded border border-gold/40 text-gold text-xs uppercase tracking-wider hover:bg-gold/10 text-center">
                {t('view_my_store')}
              </Link>
            </div>
          ) : (
            <div>
              <div className="text-sm text-gray-300 mb-3">{t('not_elite_yet')}</div>
              <p className="text-xs text-gray-400 mb-2">{t('requirements_label')}</p>
              <ul className="text-xs text-gray-300 space-y-1 mb-3">
                <li>• {t('req_bio_photo')}</li>
                <li>• {t('req_credentials')}</li>
                <li>• {t('req_5_plays')}</li>
              </ul>
              <button disabled className="w-full px-4 py-2 rounded border border-gold/30 text-gold text-xs uppercase tracking-wider opacity-60">
                {t('request_verification')}
              </button>
            </div>
          )}
        </div>
      </div>

      {achievements && achievements.unlocked && (
        <div className="mt-8 bg-card-steel border border-gold/30 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-h uppercase text-lg text-gold flex items-center gap-2">
              <Trophy className="w-5 h-5" /> {t('my_achievements')} · {achievements.unlocked.length}/{achievements.total_available}
            </h2>
            <Link to="/achievements" className="text-gold text-xs uppercase tracking-widest hover:underline">{t('view_all')} →</Link>
          </div>
          {achievements.unlocked.length > 0 ? (
            <BadgeCabinet unlocked={achievements.unlocked} catalog={achievements.unlocked.slice(0, 6).map(u => u.badge)} showLocked={false} size="md" />
          ) : (
            <p className="text-xs text-gray-500 italic">{t('no_achievements')}</p>
          )}
        </div>
      )}

      <PublicProfileEditor />

      <div className="mt-8" data-testid="profile-my-listings">
        <h2 className="font-h uppercase text-2xl mb-1 flex items-center gap-2"><Store className="w-6 h-6 text-gold" /> {t('my_publications')}</h2>
        <p className="text-xs text-gray-400 mb-4">
          {t('marketplace_seller_blurb')}
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {myPlays.filter(p => !p.is_global).map(p => {
            const isPurchased = !!p.purchased_from;
            const displayName = isPurchased ? p.name.replace(/\s*\(Comprada\)\s*$/i, '') : p.name;
            return (
            <div key={p.id} className="bg-card-steel border border-gold/30 rounded-lg p-4" data-testid={`profile-listing-${p.id}`}>
              <div className="font-h uppercase text-lg text-white mb-1">
                {displayName} {isPurchased && <span className="text-[10px] text-gold ms-1">{t('purchased_badge')}</span>}
              </div>
              <div className="text-xs text-gray-400 uppercase mb-2">{p.category} · {p.subcategory}</div>
              {p.for_sale ? (
                <>
                  <div className="text-gold font-h text-2xl mb-2"><DollarSign className="w-5 h-5 inline" />{p.price?.toFixed(2)}</div>
                  <button
                    onClick={() => unpublish(p)}
                    data-testid={`profile-unpublish-${p.id}`}
                    className="w-full text-xs px-3 py-2 rounded border border-alert/50 text-alert hover:bg-alert/10"
                  >
                    {t('unpublish_from_marketplace')}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => publish(p)}
                  data-testid={`profile-publish-${p.id}`}
                  className="w-full text-xs px-3 py-2 rounded border border-gold/40 text-gold hover:bg-gold/10"
                >
                  {t('publish_in_marketplace')}
                </button>
              )}
            </div>
            );
          })}
          {!myPlays.filter(p => !p.is_global).length && (
            <div className="col-span-full text-center text-gray-400 py-8 border-2 border-dashed border-gold/30 rounded-lg">
              {t('no_plays_yet')}. <Link to="/editor" className="text-gold underline">{t('go_to_editor')}</Link>.
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
